import React, { useEffect, useRef, useState } from "react";
import {
  Truck, NavigationArrow, CheckCircle, Warning, MapPin, MapTrifold,
  Crosshair, ArrowsClockwise, ChatCircleDots, SignOut, Phone, Lock,
  LockOpen, Play, Flag, DeviceMobile, X, Gear, ArrowsOut, ArrowsIn, Prohibit,
  CaretDown, CaretUp, Receipt,
} from "@phosphor-icons/react";
import ServiceDaysStrip from "../ServiceDaysStrip";
import { NavigateButton } from "./NavigationLauncher";

/**
 * TabletDriverHub
 *
 * Truck-mounted Android tablet layout for the Driver App. All state and
 * actions are owned by the parent <DriverApp /> and passed in via props —
 * this component is purely presentational so the existing phone layout
 * keeps working untouched.
 *
 * Layout (≥1024px):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  TOP BAR  · route · progress · clock · phone/tablet · logout │
 *   ├──────────────┬────────────────────────────────┬──────────────┤
 *   │              │                                │              │
 *   │  STOP LIST   │  CURRENT STOP DETAIL CARD      │ ACTION BAR   │
 *   │  (scroll)    │  + Complete / Issue / Nav      │ Nearest      │
 *   │              │                                │ Route Map    │
 *   │              │                                │ Refresh      │
 *   │              │                                │ Messages     │
 *   │              │                                │ Start/Finish │
 *   │              │                                │ Logout       │
 *   └──────────────┴────────────────────────────────┴──────────────┘
 *
 * Color rules (matches existing phone UI):
 *   completed → green   #2E7D32
 *   issue     → orange  #F57C00
 *   current   → bahama  #CFAB7A
 *   pending   → navy    #012340
 */
export default function TabletDriverHub({
  // identity / route
  user,
  route,
  serviceDate,
  todayLabel,

  // stops + completions
  stops,
  pendingStops,
  completedStops,
  completionMap,
  current,
  idx,
  onPickStop, // (realIdx:number) => void

  // metrics
  completedCount,
  issueCount,
  progress,

  // session
  activeSession,
  lastSession,
  sessionBusy,
  elapsedTick,
  onStart,
  onFinish,

  // actions
  onComplete,
  onIssue,
  onAcknowledge,   // (stop, reason) => Promise — no-pickup ack for off-week or suspended
  onNavigate,
  onCall,
  onFindNearest,
  findingNearest,
  nearestId,
  nearestMeta,
  onClearNearest,
  onOpenMap,
  onRefresh,
  onOpenMessages,
  onOpenSettings,
  onOpenDisposal,   // opens the disposal-ticket submission sheet
  onChangeRoute,
  onSwitchToPhone,
  onLogout,
}) {
  // Live wall-clock in the top bar — drivers want to see route start window vs current time.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const clockText = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const dateText = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  // Fullscreen toggle — hides the browser URL bar for truck-mounted tablets.
  // Must be triggered by a user gesture (button click) per browser security.
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== "undefined" && !!document.fullscreenElement,
  );
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, []);
  async function toggleFullscreen() {
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      }
    } catch {
      // Some iOS Safari versions still block requestFullscreen — the button
      // just no-ops silently in that case. "Add to Home Screen" is the
      // iPad-native answer and is documented in the driver-setup guide.
    }
  }

  // ── Kiosk Lock ─────────────────────────────────────────────────
  // Persistent lock that:
  //   * Auto-enters fullscreen
  //   * Guards the exit actions (Logout / Phone / Change Route / Settings)
  //   * Blocks browser back-swipe / back button via a pushState + popstate trap
  //   * Requires a 2-second hold to unlock so a glove tap can't accidentally exit
  const [kioskLocked, setKioskLocked] = useState(() => {
    try { return localStorage.getItem("zmex.driver.kioskLocked") === "1"; }
    catch { return false; }
  });
  const [unlockProgress, setUnlockProgress] = useState(0); // 0..100
  // Completed-stops section collapses by default so the driver's active
  // list stays uncluttered. Reopens on demand via the toggle.
  const [completedCollapsed, setCompletedCollapsed] = useState(true);

  useEffect(() => {
    try { localStorage.setItem("zmex.driver.kioskLocked", kioskLocked ? "1" : "0"); } catch { /* ignore */ }
  }, [kioskLocked]);

  // When locking, request fullscreen. When unlocking, leave FS as-is.
  useEffect(() => {
    if (!kioskLocked) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      toggleFullscreen().catch(() => {});
    }
  }, [kioskLocked]);

  // Back-button trap while locked.
  useEffect(() => {
    if (!kioskLocked) return;
    const onPop = () => {
      // Re-push our sentinel so the browser can't leave the app.
      window.history.pushState({ zmexKiosk: true }, "", window.location.href);
    };
    window.history.pushState({ zmexKiosk: true }, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [kioskLocked]);

  function toggleKiosk() {
    if (kioskLocked) return; // must unlock via hold, not tap
    setKioskLocked(true);
  }

  // Hold-to-unlock: pointerdown starts a 2s ramp, pointerup/leave cancels.
  const unlockTimer = useRef(null);
  const unlockRaf = useRef(null);
  function startUnlockHold() {
    const startedAt = Date.now();
    const tick = () => {
      const pct = Math.min(100, ((Date.now() - startedAt) / 2000) * 100);
      setUnlockProgress(pct);
      if (pct < 100) unlockRaf.current = requestAnimationFrame(tick);
    };
    unlockRaf.current = requestAnimationFrame(tick);
    unlockTimer.current = setTimeout(() => {
      setKioskLocked(false);
      setUnlockProgress(0);
    }, 2000);
  }
  function cancelUnlockHold() {
    if (unlockTimer.current) { clearTimeout(unlockTimer.current); unlockTimer.current = null; }
    if (unlockRaf.current) { cancelAnimationFrame(unlockRaf.current); unlockRaf.current = null; }
    setUnlockProgress(0);
  }

  // Wrap the exit actions so they no-op while locked.
  const safeLogout       = () => { if (!kioskLocked) onLogout && onLogout(); };
  const safeSwitchPhone  = () => { if (!kioskLocked) onSwitchToPhone && onSwitchToPhone(); };
  const safeChangeRoute  = () => { if (!kioskLocked) onChangeRoute && onChangeRoute(); };
  const safeOpenSettings = () => { if (!kioskLocked) onOpenSettings && onOpenSettings(); };

  // Display order: surface nearest at top of left list when set.
  const orderedPending = (() => {
    if (!nearestId) return pendingStops;
    const top = pendingStops.find((s) => s.id === nearestId);
    const rest = pendingStops.filter((s) => s.id !== nearestId);
    return top ? [top, ...rest] : pendingStops;
  })();

  return (
    <div
      className="h-screen overflow-hidden bg-[#0a0f1a] text-white flex flex-col"
      style={{
        // Respect Android system bars + keep the app fully visible on any
        // tablet size. `dvh` handles WebViews that hide the URL bar on scroll.
        height: "100dvh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
      data-testid="driver-hub-tablet"
    >
      {/* ─── TOP BAR ─────────────────────────────────────────────── */}
      <header className="bg-[#012340] border-b-2 border-[#CFAB7A]/40 flex-shrink-0">
        <div className="flex items-center gap-3 px-4 py-3.5 flex-nowrap min-w-0">
          <div className="w-12 h-12 rounded-sm bg-[#CFAB7A] grid place-items-center text-[#012340] flex-shrink-0">
            <Truck size={24} weight="bold" />
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <button
              onClick={safeChangeRoute}
              disabled={kioskLocked}
              className="text-[10px] uppercase tracking-[0.25em] text-[#CFAB7A] hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline block whitespace-nowrap"
              data-testid="hub-change-route"
            >
              ← Change route
            </button>
            <div className="font-heading font-black text-xl truncate leading-tight mt-0.5">
              {route?.name || "No route"}
            </div>
            <div className="text-[11px] text-white/60 font-mono truncate mt-0.5">
              {dateText} · Hi <span className="text-white font-bold">{user?.name}</span>
            </div>
          </div>

          {/* Progress stats — compact, always inline */}
          <div className="flex items-stretch gap-2 flex-shrink-0" data-testid="hub-progress-stats">
            <Stat label="Done"      value={completedCount}       accent="#7FC683" testid="hub-stat-completed" />
            <Stat label="Remain"    value={pendingStops.length}  accent="#CFAB7A" testid="hub-stat-remaining" />
            <Stat label="Issues"    value={issueCount}           accent={issueCount > 0 ? "#FFB877" : "#5C738A"} testid="hub-stat-issues" />
            <Stat label="Progress"  value={`${progress}%`}       accent="#CFAB7A" testid="hub-stat-progress" />
          </div>

          {/* Clock */}
          <div className="text-right flex-shrink-0 px-1" data-testid="hub-clock">
            <div className="font-heading font-black text-3xl tabular-nums leading-none">{clockText}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#CFAB7A] mt-1">Time</div>
          </div>

          {/* Mode toggle + logout — icon-only on tablet, labels appear ≥1400px */}
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-1">
            <button
              onClick={toggleFullscreen}
              className={`inline-flex items-center gap-1.5 px-3 py-3 rounded-sm border text-[11px] uppercase tracking-[0.2em] font-bold transition-all ${
                isFullscreen
                  ? "bg-[#CFAB7A] border-[#CFAB7A] text-[#012340]"
                  : "border-white/15 text-white/75 hover:text-white hover:border-white/35"
              }`}
              data-testid="hub-fullscreen"
              title={isFullscreen ? "Exit full screen" : "Enter full screen (hides browser URL bar)"}
            >
              {isFullscreen ? <ArrowsIn size={18} weight="bold" /> : <ArrowsOut size={18} weight="bold" />}
              <span className="hidden 2xl:inline">{isFullscreen ? "Exit FS" : "Full Screen"}</span>
            </button>

            {/* Kiosk lock: tap to engage; hold "Hold to Unlock" for 2s to release. */}
            {!kioskLocked ? (
              <button
                onClick={toggleKiosk}
                className="inline-flex items-center gap-1.5 px-3 py-3 rounded-sm border border-white/15 text-white/75 hover:text-white hover:border-white/35 text-[11px] uppercase tracking-[0.2em] font-bold"
                data-testid="hub-kiosk-lock"
                title="Lock the app in kiosk mode — blocks back/logout/mode-switch until unlocked"
              >
                <Lock size={18} weight="bold" />
                <span className="hidden 2xl:inline">Kiosk Lock</span>
              </button>
            ) : (
              <button
                onPointerDown={startUnlockHold}
                onPointerUp={cancelUnlockHold}
                onPointerLeave={cancelUnlockHold}
                onPointerCancel={cancelUnlockHold}
                onTouchStart={startUnlockHold}
                onTouchEnd={cancelUnlockHold}
                onTouchCancel={cancelUnlockHold}
                className="relative inline-flex items-center gap-1.5 px-3.5 py-3 rounded-sm border border-[#CFAB7A] bg-[#CFAB7A]/25 text-[#CFAB7A] text-[11px] uppercase tracking-[0.2em] font-bold overflow-hidden select-none"
                data-testid="hub-kiosk-unlock"
                title="Hold for 2 seconds to unlock"
              >
                <span
                  className="absolute inset-0 bg-[#CFAB7A] transition-[width] duration-75 ease-linear pointer-events-none"
                  style={{ width: `${unlockProgress}%` }}
                  data-testid="hub-kiosk-unlock-progress"
                />
                <span className="relative flex items-center gap-1.5 mix-blend-difference">
                  <LockOpen size={18} weight="bold" />
                  <span className="hidden lg:inline">Hold to Unlock</span>
                </span>
              </button>
            )}

            <button
              onClick={safeOpenSettings}
              disabled={kioskLocked}
              className="inline-flex items-center gap-1.5 px-3 py-3 rounded-sm border border-white/15 text-white/75 hover:text-white hover:border-white/35 text-[11px] uppercase tracking-[0.2em] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="hub-settings"
              title={kioskLocked ? "Unlock kiosk to access settings" : "Driver settings (navigation preference, etc.)"}
            >
              <Gear size={18} weight="bold" />
            </button>
            <button
              onClick={safeSwitchPhone}
              disabled={kioskLocked}
              className="hidden 2xl:inline-flex items-center gap-1.5 px-3 py-3 rounded-sm border border-white/15 text-white/75 hover:text-white hover:border-white/35 text-[11px] uppercase tracking-[0.2em] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="hub-switch-phone"
              title={kioskLocked ? "Unlock kiosk to switch layout" : "Switch to phone layout"}
            >
              <DeviceMobile size={18} weight="bold" /> Phone
            </button>
            <button
              onClick={safeLogout}
              disabled={kioskLocked}
              className="inline-flex items-center gap-1.5 px-3 py-3 rounded-sm bg-[#D32F2F]/15 border border-[#D32F2F]/40 text-[#FF8A80] hover:bg-[#D32F2F]/25 text-[11px] uppercase tracking-[0.2em] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="hub-logout"
              title={kioskLocked ? "Unlock kiosk to log out" : "Sign out of the driver app"}
            >
              <SignOut size={18} weight="bold" />
              <span className="hidden xl:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Kiosk-locked hint strip */}
        {kioskLocked && (
          <div
            className="bg-[#CFAB7A]/10 border-t border-[#CFAB7A]/30 text-[#CFAB7A] text-[10px] uppercase tracking-[0.28em] font-bold px-5 py-1.5 flex items-center gap-2"
            data-testid="hub-kiosk-banner"
          >
            <Lock size={12} weight="bold" />
            Kiosk mode active — hold <span className="text-white">Hold to Unlock</span> for 2s to exit.
            Back / logout / mode-switch are disabled.
          </div>
        )}

        {/* Progress bar strip */}
        <div className="h-2 bg-white/5">
          <div
            className="h-full bg-gradient-to-r from-[#CFAB7A] to-[#E8C896] transition-all duration-300"
            style={{ width: `${progress}%` }}
            data-testid="hub-progress-bar"
          />
        </div>
      </header>

      {/* ─── BODY: 3-column tablet grid (each pane scrolls independently) ─ */}
      <div className="flex-1 min-h-0 grid gap-0" style={{ gridTemplateColumns: "minmax(300px, 380px) 1fr minmax(180px, 220px)" }}>

        {/* ── LEFT: scrollable stop list ──────────────── */}
        <aside className="bg-[#0f1622] border-r border-white/10 flex flex-col min-h-0" data-testid="hub-stop-list">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CFAB7A]">
              Active Stops <span className="text-white font-mono">{pendingStops.length}</span>
            </div>
            {nearestId && (
              <button
                onClick={onClearNearest}
                className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-white"
                data-testid="hub-nearest-clear"
              >
                Clear Nearest
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="hub-stop-list-scroll">
            {orderedPending.length === 0 && completedStops.length === 0 && (
              <div className="text-center text-white/55 text-sm py-10">No stops on this route.</div>
            )}

            {orderedPending.map((s) => {
              const realIdx = pendingStops.findIndex((p) => p.id === s.id);
              const c = completionMap[s.id];
              const isCurrent = realIdx === idx;
              const isIssue = c?.status === "issue";
              const isNearest = s.id === nearestId;
              const isSuspended = !!s.suspended;
              const isOffWeek = s.rotation_status === "off_week";

              const bg = isCurrent
                ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A] ring-2 ring-[#CFAB7A]/40"
                : isIssue
                  ? "bg-[#F57C00]/15 border-[#F57C00]/50 text-white"
                  : isSuspended
                    ? "bg-[#D32F2F]/10 border-[#D32F2F]/50 text-white"
                    : isOffWeek
                      ? "bg-[#F57C00]/10 border-[#F57C00]/50 text-white"
                      : "bg-[#0a1422] border-white/10 hover:border-[#CFAB7A]/40 text-white";

              return (
                <button
                  key={s.id}
                  onClick={() => onPickStop(realIdx)}
                  className={`w-full text-left rounded-sm border-2 px-3 py-3 transition ${bg}`}
                  data-testid={`hub-stop-row-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-sm grid place-items-center font-heading font-black text-base flex-shrink-0 ${
                      isIssue ? "bg-[#F57C00] text-white" :
                      isCurrent ? "bg-[#012340] text-[#CFAB7A]" :
                      isSuspended ? "bg-[#D32F2F] text-white" :
                      isOffWeek ? "bg-[#F57C00]/80 text-white" :
                      "bg-[#CFAB7A] text-[#012340]"
                    }`}>
                      {isIssue ? "!" : isSuspended ? "⊘" : isOffWeek ? "↻" : realIdx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-heading font-bold text-base truncate">{s.business_name}</div>
                        {isNearest && (
                          <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#012340] text-[#CFAB7A] px-1.5 py-0.5 rounded-sm">Nearest</span>
                        )}
                        {isSuspended && !isNearest && (
                          <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#D32F2F] text-white px-1.5 py-0.5 rounded-sm">Suspended</span>
                        )}
                        {isOffWeek && !isSuspended && !isNearest && (
                          <span className="text-[9px] font-black uppercase tracking-[0.18em] bg-[#F57C00] text-[#012340] px-1.5 py-0.5 rounded-sm">Skip · Off Wk</span>
                        )}
                      </div>
                      <div className={`text-xs truncate ${isCurrent ? "text-[#012340]/80" : "text-white/65"}`}>
                        {s.service_address}
                      </div>
                      <div className={`text-[11px] mt-1 font-mono ${isCurrent ? "text-[#012340]/70" : "text-[#CFAB7A]"}`}>
                        {s.waste_stream || "MSW"} · {s.container_size || s.dumpster_size}yd³
                        {isNearest && nearestMeta ? ` · ${nearestMeta.distance_mi.toFixed(2)} mi` : ""}
                      </div>
                      {s.service_days && s.service_days.length > 0 && (
                        <div className="mt-1.5 max-w-[200px]">
                          <ServiceDaysStrip days={s.service_days || []} compact label={null} />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Completed section — collapsed by default so the driver's
                active list stays clean. Tap the header to expand. */}
            {completedStops.length > 0 && (
              <div className="pt-3" data-testid="hub-completed-section">
                <button
                  type="button"
                  onClick={() => setCompletedCollapsed((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-[#2E7D32]/15 border border-[#2E7D32]/40 text-[#7FC683] hover:bg-[#2E7D32]/25 transition"
                  data-testid="hub-completed-toggle"
                >
                  <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em]">
                    <CheckCircle size={13} weight="fill" />
                    Completed · {completedStops.length}
                  </div>
                  {completedCollapsed
                    ? <CaretDown size={14} weight="bold" />
                    : <CaretUp   size={14} weight="bold" />}
                </button>
                {!completedCollapsed && (
                  <div className="mt-2 space-y-2">
                    {completedStops.map((s) => {
                      const cs = completionMap[s.id]?.status;
                      const isAck = cs === "acknowledged";
                      const ackReason = completionMap[s.id]?.ack_reason;
                      // Style the pill by outcome so the driver can see at a
                      // glance whether the stop was a real pickup, an issue
                      // report, or a no-pickup acknowledgement.
                      const label = cs === "issue"
                        ? "!"
                        : isAck && ackReason === "suspended"
                          ? "⊘"
                          : isAck
                            ? "↻"
                            : "✓";
                      const rowCls = cs === "issue"
                        ? "bg-[#F57C00]/10 border-[#F57C00]/40"
                        : isAck && ackReason === "suspended"
                          ? "bg-[#D32F2F]/10 border-[#D32F2F]/40"
                          : isAck
                            ? "bg-[#F57C00]/10 border-[#F57C00]/40"
                            : "bg-[#2E7D32]/10 border-[#2E7D32]/40";
                      return (
                        <div
                          key={s.id}
                          className={`${rowCls} border rounded-sm px-3 py-2.5 flex items-center gap-3`}
                          data-testid={`hub-completed-row-${s.id}`}
                        >
                          <div className="w-9 h-9 rounded-sm bg-[#2E7D32] text-white grid place-items-center font-bold flex-shrink-0">{label}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold truncate">{s.business_name}</div>
                            <div className="text-[11px] text-white/55 truncate">{s.service_address}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* ── CENTER: current stop detail ─────────────── */}
        <main className="bg-[#0a0f1a] overflow-y-auto min-h-0" data-testid="hub-stop-detail">
          {!route ? (
            <div className="h-full grid place-items-center p-10 text-center text-white/65">
              <div>
                <Truck size={48} weight="duotone" className="text-[#CFAB7A] mx-auto mb-3" />
                <div className="font-heading font-bold text-lg mb-1">No route assigned for today.</div>
                <div className="text-sm">Contact your dispatcher.</div>
              </div>
            </div>
          ) : !current ? (
            <div className="h-full grid place-items-center p-10 text-center">
              {stops.length === 0 ? (
                <div>
                  <MapPin size={64} weight="duotone" className="text-[#CFAB7A] mx-auto mb-4" />
                  <div className="font-heading font-black text-3xl mb-2">No stops today</div>
                  <div className="text-white/65">
                    Nothing is scheduled on this route for {todayLabel.split(",")[0] || "today"}.
                  </div>
                </div>
              ) : (
                <div>
                  <CheckCircle size={64} weight="fill" className="text-[#7FC683] mx-auto mb-4" />
                  <div className="font-heading font-black text-3xl mb-2">Route Complete</div>
                  <div className="text-white/65">All {stops.length} stops handled. Great work!</div>
                  {activeSession?.start_time && (
                    <button
                      onClick={onFinish}
                      disabled={sessionBusy}
                      className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-6 py-4 font-heading font-black text-lg disabled:opacity-50"
                      data-testid="hub-finish-route-empty"
                    >
                      <Flag size={20} weight="bold" /> {sessionBusy ? "Finishing…" : "Finish Route"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 max-w-3xl">
              {/* Stop position */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-[#CFAB7A] text-[#012340] w-10 h-10 grid place-items-center rounded-sm font-heading font-black text-lg">
                    {idx + 1}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">
                    Stop {idx + 1} of {pendingStops.length}
                  </span>
                  {current.id === nearestId && (
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] bg-[#CFAB7A] text-[#012340] px-2 py-1 rounded-sm">
                      Nearest
                    </span>
                  )}
                </div>
              </div>

              {/* Business name + address */}
              <h2 className="font-heading font-black text-4xl leading-tight" data-testid="hub-current-name">
                {current.business_name}
              </h2>
              <div className="mt-2 flex items-start gap-2 text-white/80 text-lg">
                <MapPin size={20} weight="bold" className="text-[#CFAB7A] mt-1 flex-shrink-0" />
                <span data-testid="hub-current-address">{current.service_address}</span>
              </div>
              {current.id === nearestId && nearestMeta && (
                <div className="mt-2 text-[#CFAB7A] font-mono text-sm">
                  {nearestMeta.distance_mi.toFixed(2)} mi from your current location
                </div>
              )}

              {/* Pills — Stream / Size / Frequency (derived from service days).
                  Frequency is computed live: N service days → "Nx/Week"; a
                  single day maps to "Weekly", empty maps to "—". This makes
                  the driver's on-truck view match what dispatch entered on the
                  customer's service line. */}
              <div className="mt-5 grid grid-cols-3 gap-2" data-testid="hub-stop-pills">
                <BigPill label="Stream" value={current.waste_stream || "MSW"} />
                <BigPill
                  label="Size"
                  value={`${current.container_size || current.dumpster_size} yd³${(current.quantity || 1) > 1 ? ` ×${current.quantity}` : ""}`}
                />
                <BigPill
                  label="Frequency"
                  value={(() => {
                    const n = (current.service_days || []).length;
                    if (n === 0) return "—";
                    const freq = current.service_frequency || "weekly";
                    if (freq === "biweekly")     return n === 1 ? "Bi-Weekly" : `Bi-Weekly · ${n}x`;
                    if (freq === "monthly")      return n === 1 ? "Monthly"   : `Monthly · ${n}x`;
                    if (freq === "twice_weekly") return "2x/Week";
                    if (freq === "three_weekly") return "3x/Week";
                    return n === 1 ? "Weekly" : `${n}x/Week`;
                  })()}
                  data-testid="hub-frequency"
                />
              </div>

              {/* Weekly service-day strip — highlights the days this container
                  is serviced and marks today so the driver can confirm at a glance. */}
              {current.service_days && current.service_days.length > 0 && (
                <div className="mt-4" data-testid="hub-service-days">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#CFAB7A] font-bold mb-2">
                    Weekly Service Days
                  </div>
                  <ServiceDaysStrip days={current.service_days || []} label={null} />
                </div>
              )}

              {current.gate_code && (
                <div className="mt-4 bg-[#CFAB7A]/15 border border-[#CFAB7A]/40 rounded-sm px-4 py-3 flex items-center gap-2">
                  <Lock size={18} weight="bold" className="text-[#CFAB7A]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CFAB7A]">Gate Code</span>
                  <span className="ml-auto font-heading font-black text-white text-lg">{current.gate_code}</span>
                </div>
              )}

              {current.service_notes && (
                <div className="mt-3 text-sm bg-white/5 border border-white/10 rounded-sm p-4">
                  <div className="font-bold uppercase tracking-[0.2em] text-[#CFAB7A] text-[10px] mb-1">Notes</div>
                  <div className="text-white/90">{current.service_notes}</div>
                </div>
              )}

              {/* Primary actions — big tablet-friendly. Renders one of THREE
                  states depending on the stop:
                    - Suspended customer   → RED card (Acknowledge + Service Anyway)
                    - Bi-weekly off week   → AMBER card (Acknowledge + Force Pickup)
                    - Normal / on-week     → gold Complete + red Issue
                  Once a completion (any status) exists we swap to a status pill. */}
              {completionMap[current.id] ? (
                <div className={`mt-5 rounded-sm px-4 py-4 text-base font-bold ${
                  completionMap[current.id].status === "completed"
                    ? "bg-[#2E7D32]/15 text-[#7FC683] border border-[#2E7D32]/40"
                    : completionMap[current.id].status === "acknowledged"
                      ? (completionMap[current.id].ack_reason === "suspended"
                          ? "bg-[#D32F2F]/15 text-[#FF8A80] border border-[#D32F2F]/40"
                          : "bg-[#F57C00]/15 text-[#FFB877] border border-[#F57C00]/40")
                      : "bg-[#F57C00]/15 text-[#FFB877] border border-[#F57C00]/40"
                }`} data-testid="hub-current-status">
                  {completionMap[current.id].status === "completed"
                    ? "✓ Completed at this stop"
                    : completionMap[current.id].status === "acknowledged"
                      ? (completionMap[current.id].ack_reason === "suspended"
                          ? `⊘ Suspension acknowledged · no pickup`
                          : `↻ Off-week acknowledged · no pickup`)
                      : `⚠ Issue reported: ${(completionMap[current.id].issue_type || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}`}
                </div>
              ) : current.suspended ? (
                <div className="mt-5 rounded-sm border-2 border-[#D32F2F] bg-[#D32F2F]/[0.12] p-4 space-y-3" data-testid="hub-suspended-panel">
                  <div className="flex items-center gap-3">
                    <Prohibit size={26} weight="bold" className="text-[#FF8A80] flex-shrink-0" />
                    <div className="font-heading font-black uppercase tracking-[0.2em] text-[#FF8A80]">
                      Suspended · {current.suspension_reason || "Payment"}
                    </div>
                  </div>
                  {current.suspension_note && (
                    <div className="text-sm text-white/85 italic bg-black/25 rounded-sm px-3 py-2">
                      "{current.suspension_note}"
                    </div>
                  )}
                  <div className="text-xs text-white/70 leading-relaxed">
                    No pickup required. Tap <b>Acknowledge</b> to log a no-service, or <b>Service Anyway</b> if the customer paid on the truck.
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <button
                      onClick={() => onAcknowledge && onAcknowledge(current, "suspended")}
                      className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#D32F2F] text-white px-5 py-4 font-heading font-black text-base hover:brightness-110"
                      data-testid="hub-acknowledge-suspended-btn"
                    >
                      <Prohibit size={22} weight="bold" /> Acknowledge
                    </button>
                    <button
                      onClick={onComplete}
                      className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-white/40 text-white px-5 py-4 font-heading font-black text-base hover:bg-white/10"
                      data-testid="hub-service-anyway-btn"
                    >
                      <CheckCircle size={22} weight="bold" /> Service Anyway
                    </button>
                  </div>
                </div>
              ) : current.rotation_status === "off_week" ? (
                <div className="mt-5 rounded-sm border-2 border-[#F57C00] bg-[#F57C00]/[0.10] p-4 space-y-3" data-testid="hub-offweek-panel">
                  <div className="flex items-center gap-3">
                    <ArrowsClockwise size={26} weight="bold" className="text-[#FFB877] flex-shrink-0" />
                    <div className="font-heading font-black uppercase tracking-[0.2em] text-[#FFB877]">
                      Skip Today · Bi-Weekly Off Week
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm text-white/85">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Last serviced</div>
                      <div className="font-mono tabular-nums">{current.last_serviced_at || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Return next</div>
                      <div className="font-mono tabular-nums text-[#FFB877]">{current.next_pickup_date || "—"}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <button
                      onClick={() => onAcknowledge && onAcknowledge(current, "off_week")}
                      className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#F57C00] text-[#012340] px-5 py-4 font-heading font-black text-base hover:brightness-105"
                      data-testid="hub-acknowledge-offweek-btn"
                    >
                      <ArrowsClockwise size={22} weight="bold" /> Acknowledge
                    </button>
                    <button
                      onClick={onComplete}
                      className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-white/40 text-white px-5 py-4 font-heading font-black text-base hover:bg-white/10"
                      data-testid="hub-force-pickup-btn"
                    >
                      <CheckCircle size={22} weight="bold" /> Force Pickup
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    onClick={onComplete}
                    className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#CFAB7A] text-[#012340] px-6 py-5 font-heading font-black text-lg hover:brightness-105"
                    data-testid="hub-complete-btn"
                  >
                    <CheckCircle size={22} weight="bold" /> Complete
                  </button>
                  <button
                    onClick={onIssue}
                    className="inline-flex items-center justify-center gap-2 rounded-sm border-2 border-[#D32F2F] text-[#FF8A80] px-6 py-5 font-heading font-black text-lg hover:bg-[#D32F2F]/15"
                    data-testid="hub-issue-btn"
                  >
                    <Warning size={22} weight="bold" /> Issue
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <NavigateButton stop={current} variant="tablet" />
                    <button
                      onClick={() => current.phone && onCall(current.phone)}
                      disabled={!current.phone}
                      className="inline-flex flex-col items-center justify-center gap-1 rounded-sm bg-white/5 border border-white/15 text-white px-3 py-5 font-bold text-sm hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="hub-call-btn"
                    >
                      <Phone size={22} weight="bold" />
                      <span>Call</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>

        {/* ── RIGHT: big action bar ───────────────────── */}
        <aside className="bg-[#0f1622] border-l border-white/10 flex flex-col p-3 gap-2 overflow-y-auto min-h-0" data-testid="hub-action-bar">
          {/* Session button */}
          {route && (
            <SessionButton
              activeSession={activeSession}
              lastSession={lastSession}
              busy={sessionBusy}
              elapsedTick={elapsedTick}
              onStart={onStart}
              onFinish={onFinish}
            />
          )}

          <ActionBtn
            icon={<Crosshair size={26} weight="bold" />}
            label={findingNearest ? "Locating…" : "Nearest"}
            sublabel="Closest stop"
            onClick={onFindNearest}
            disabled={findingNearest || pendingStops.length === 0}
            testid="hub-nearest-btn"
            tone="primary"
          />
          <ActionBtn
            icon={<MapTrifold size={26} weight="bold" />}
            label="Route Map"
            sublabel="All stops + GPS"
            onClick={onOpenMap}
            disabled={!stops.length}
            testid="hub-map-btn"
            tone="primary"
          />
          <ActionBtn
            icon={<Receipt size={26} weight="bold" />}
            label="Disposal"
            sublabel="Log a dump ticket"
            onClick={onOpenDisposal}
            testid="hub-disposal-btn"
            tone="primary"
          />
          <ActionBtn
            icon={<ArrowsClockwise size={26} weight="bold" />}
            label="Refresh"
            sublabel="Reload route"
            onClick={onRefresh}
            testid="hub-refresh-btn"
            tone="neutral"
          />
          <ActionBtn
            icon={<ChatCircleDots size={26} weight="bold" />}
            label="Messages"
            sublabel="From dispatch"
            onClick={onOpenMessages}
            testid="hub-messages-btn"
            tone="neutral"
          />
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="px-3 py-1.5 border-l border-white/10 first:border-l-0 min-w-[52px]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 font-bold">{label}</div>
      <div className="font-heading font-black text-2xl tabular-nums leading-none mt-1" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function BigPill({ label, value, subtle = false, "data-testid": testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-sm px-3 py-3 border ${subtle ? "bg-white/3 border-white/10" : "bg-[#012340] border-[#CFAB7A]/30"}`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#CFAB7A] font-bold">{label}</div>
      <div className="text-white font-heading font-bold text-lg mt-0.5 truncate">{value}</div>
    </div>
  );
}

function ActionBtn({ icon, label, sublabel, onClick, disabled, testid, tone = "neutral" }) {
  const toneCls = tone === "primary"
    ? "bg-[#CFAB7A]/15 hover:bg-[#CFAB7A]/25 border-[#CFAB7A]/40 text-[#CFAB7A]"
    : "bg-white/5 hover:bg-white/10 border-white/15 text-white";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex flex-col items-center justify-center gap-1 rounded-sm border-2 px-3 py-4 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${toneCls}`}
    >
      {icon}
      <span className="text-sm font-heading font-black uppercase tracking-wider leading-none mt-1">{label}</span>
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">{sublabel}</span>
    </button>
  );
}

function SessionButton({ activeSession, lastSession, busy, elapsedTick, onStart, onFinish }) {
  // Use a `now` state ticking every second so the elapsed clock updates without
  // running impure reads of Date.now() inside the render path. elapsedTick is
  // still accepted as a redundant trigger from the parent for safety.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!activeSession?.start_time) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [activeSession?.start_time, elapsedTick]);

  if (activeSession?.start_time) {
    const startTs = new Date(activeSession.start_time).getTime();
    const elapsedH = Math.max(0, (now - startTs) / 3600000);
    const h = Math.floor(elapsedH);
    const m = Math.round((elapsedH - h) * 60);
    const elapsedText = elapsedH < 1 / 60
      ? `${Math.max(0, Math.round(elapsedH * 3600))}s`
      : `${h}h ${String(m).padStart(2, "0")}m`;
    return (
      <button
        onClick={onFinish}
        disabled={busy}
        data-testid="hub-finish-route-btn"
        className="w-full flex flex-col items-center gap-0.5 rounded-sm border-2 border-[#CFAB7A] bg-[#CFAB7A] text-[#012340] px-3 py-4 font-bold disabled:opacity-50 hover:brightness-105"
      >
        <Flag size={26} weight="bold" />
        <span className="text-sm font-heading font-black uppercase tracking-wider mt-1">
          {busy ? "Finishing…" : "Finish Route"}
        </span>
        <span className="text-[11px] font-mono tabular-nums">{elapsedText}</span>
      </button>
    );
  }

  if (lastSession?.status === "completed") {
    return (
      <div className="w-full text-center rounded-sm border-2 border-[#2E7D32]/40 bg-[#2E7D32]/15 px-3 py-4" data-testid="hub-route-done">
        <CheckCircle size={26} weight="fill" className="text-[#7FC683] mx-auto" />
        <div className="text-sm font-heading font-black uppercase tracking-wider text-[#7FC683] mt-1">Done</div>
      </div>
    );
  }

  return (
    <button
      onClick={onStart}
      disabled={busy}
      data-testid="hub-start-route-btn"
      className="w-full flex flex-col items-center gap-0.5 rounded-sm border-2 border-[#2E7D32] bg-[#2E7D32] text-white px-3 py-4 font-bold disabled:opacity-50 hover:brightness-110"
    >
      <Play size={26} weight="fill" />
      <span className="text-sm font-heading font-black uppercase tracking-wider mt-1">
        {busy ? "Starting…" : "Start Route"}
      </span>
      <span className="text-[10px] uppercase tracking-[0.18em] opacity-80">Begin clock</span>
    </button>
  );
}

/** Lightweight Messages "Coming Soon" sheet — opened by the Messages action button. */
export function DriverMessagesSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 grid place-items-center p-4"
      onClick={onClose}
      data-testid="hub-messages-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f1622] text-white border border-white/10 rounded-sm w-full max-w-md p-6"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Dispatch</div>
            <h3 className="font-heading font-black text-2xl mt-1">Messages</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-sm hover:bg-white/10"
            data-testid="hub-messages-close"
            aria-label="Close messages"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="mt-6 text-center py-8">
          <ChatCircleDots size={48} weight="duotone" className="text-[#CFAB7A] mx-auto mb-3" />
          <div className="font-bold text-base text-white mb-1">Driver ↔ Dispatch chat coming soon</div>
          <div className="text-sm text-white/65 max-w-sm mx-auto">
            Real-time messaging between drivers and dispatchers is being built. For now, call dispatch directly.
          </div>
        </div>
      </div>
    </div>
  );
}
